import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { corners } from './genericBlock';
import { toLayout, type PlanFrame } from './planFrame';
import readings from './ursulaPlan.json';

export const URSULA = readings;
export type UrsulaPart = typeof readings.chapel;

export function ursulaFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Ursula convent lacks its component anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export function ursulaPartFrame(plan: PlanFrame, part: UrsulaPart): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

export function ursulaPartRect(part: UrsulaPart) {
  return { u0: -part.length / 2, u1: part.length / 2, v0: -part.width / 2, v1: part.width / 2 };
}

export function ursulaObstacles(landmark: Landmark): Ring[] {
  const plan = ursulaFrame(landmark),
    reading = readings;
  const parts = [reading.chapel, ...reading.ranges, ...reading.rentals].flatMap((part) => {
    const frame = ursulaPartFrame(plan, part),
      rect = ursulaPartRect(part),
      rooms = part.passage
        ? [
            { ...rect, u1: -part.passage / 2 },
            { ...rect, u0: part.passage / 2 },
          ]
        : [rect];
    return rooms.map((room) => corners(room).map((p) => toLayout(frame, p)));
  });
  parts.push(corners(readings.sideWing).map((p) => toLayout(plan, p)));
  return parts;
}

/** Cultivated court and graded access of Ursula, separate from the rental houses. */
export function ursulaFloors(landmark: Landmark) {
  const reading = readings,
    plan = ursulaFrame(landmark),
    beds = [],
    g = reading.garden;
  for (let row = 0; row < g.rows; row++)
    for (let col = 0; col < g.columns; col++) {
      const du = (g.u1 - g.u0) / g.columns,
        dv = (g.v1 - g.v0) / g.rows;
      beds.push({
        ring: corners({
          u0: g.u0 + col * du + 0.4,
          u1: g.u0 + (col + 1) * du - 0.4,
          v0: g.v0 + row * dv + 0.4,
          v1: g.v0 + (row + 1) * dv - 0.4,
        }).map((p) => toLayout(plan, p)),
        height: 0.035,
      });
    }
  const entry = reading.ranges.find((p) => p.id === reading.entryRange)!;
  return {
    beds,
    court: { ring: reading.court.map((p): Vec2 => toLayout(plan, [p[0]!, p[1]!])), height: 0.03 },
    entry: {
      ring: corners({
        ...ursulaPartRect(entry),
        u0: -entry.passage / 2,
        u1: entry.passage / 2,
      }).map((p) => toLayout(ursulaPartFrame(plan, entry), p)),
      part: entry,
    },
  };
}

/** The west portal meets local quay ground, then rises through the passage to the court. */
export function ursulaEntryHeight(
  frame: PlanFrame,
  part: UrsulaPart,
  p: Vec2,
  terrain: (p: Vec2) => number,
  base: number
): number {
  const dx = p[0] - frame.origin[0],
    dy = p[1] - frame.origin[1],
    v = -dx * Math.sin(frame.heading) + dy * Math.cos(frame.heading),
    t = Math.max(0, Math.min(1, (part.width / 2 - v) / part.width)),
    start = terrain(toLayout(frame, [0, part.width / 2]));
  return start + (base - start) * t + 0.03;
}
